import {capitalize} from "@mui/material";
import {useSnackbar} from "notistack";
import {useMutation, useQueryClient} from "react-query";
import {useTranslation} from "react-i18next";

import {
    useCreateIcpFallbackPrompt,
    useDeactivateIcpFallbackPrompt,
    useDeletePrompt,
    useSetActiveIcpFallbackPrompt,
    useUpdatePrompt,
} from "../../client/hooks";
import {usePromptHandlingBase, useErrorMessage} from "../aufgaben/useAufgabenPromptHandling";

export function usePhonenumberPromptHandling(phoneNumberId: string) {
    const {t: translation} = useTranslation('phonenumbers');
    const queryClient = useQueryClient();
    const {enqueueSnackbar} = useSnackbar();
    const formatError = useErrorMessage();

    const createPrompt = useCreateIcpFallbackPrompt();
    const updatePrompt = useUpdatePrompt();
    const setActivePrompt = useSetActiveIcpFallbackPrompt();
    const deactivatePrompt = useDeactivateIcpFallbackPrompt();
    const deletePromptFn = useDeletePrompt();


    const deletePromptWrapper = (_entityId: string, promptId: string) => deletePromptFn(promptId);

    const base = usePromptHandlingBase(
        phoneNumberId,
        [
            ["api", "v1", "phonenumbers", phoneNumberId, "icp-fallback-prompts"],
            ["api", "v1", "phonenumbers", phoneNumberId],
            ["api", "v1", "phonenumbers"],
            ["api", "v1", "prompts"],
        ],
        createPrompt,
        updatePrompt,
        setActivePrompt,
        deletePromptWrapper,
    );

    const deactivatePromptMutation = useMutation(
        () => deactivatePrompt(phoneNumberId, '_unused'),
        {
            onSuccess: async () => {
                await Promise.all([
                    queryClient.invalidateQueries(["api", "v1", "phonenumbers", phoneNumberId, "icp-fallback-prompts"]),
                    queryClient.invalidateQueries(["api", "v1", "phonenumbers", phoneNumberId]),
                    queryClient.invalidateQueries(["api", "v1", "phonenumbers"]),
                    queryClient.invalidateQueries(["api", "v1", "prompts"]),
                ]);
                enqueueSnackbar(capitalize(translation('phonenumber-inbound-config-deactivate-successful')), {variant: 'success'});
            },
            onError: (err) => {
                enqueueSnackbar(formatError(err, capitalize(translation('phonenumber-inbound-config-deactivate-failed'))), {variant: 'error'});
            },
        }
    );

    return {
        ...base,
        deactivatePromptMutation,
    };
}
